import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Bell, Plus, Edit2, Trash2, Loader2, Mail, Phone, MessageCircle } from 'lucide-react';
import { apiRequest } from '@/utils/api-request';
import { showToast } from '@/components/ui/Toast';

interface NotificationContact {
    id: number;
    slate_id: number;
    name: string;
    designation: string | null;
    phone: string | null;
    email: string | null;
    notify_email: boolean;
    notify_whatsapp: boolean;
    is_active: boolean;
    created_at: string;
    updated_at: string;
}

interface ContactFormData {
    name: string;
    designation: string;
    phone: string;
    email: string;
    notify_email: boolean;
    notify_whatsapp: boolean;
    is_active: boolean;
}

const initialFormData: ContactFormData = {
    name: '',
    designation: '',
    phone: '',
    email: '',
    notify_email: true,
    notify_whatsapp: false,
    is_active: true,
};

const NotificationCenter: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const [contacts, setContacts] = useState<NotificationContact[]>([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [editingContact, setEditingContact] = useState<NotificationContact | null>(null);
    const [formData, setFormData] = useState<ContactFormData>(initialFormData);
    const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);

    useEffect(() => {
        fetchContacts();
    }, [id]);

    const fetchContacts = async () => {
        try {
            setLoading(true);
            const response = await apiRequest(`/schools/${id}/notification-contacts`);
            if (response.success) {
                setContacts(response.data?.contacts || []);
            }
        } catch (error) {
            console.error('Error fetching contacts:', error);
            showToast('Failed to load notification contacts', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleOpenModal = (contact?: NotificationContact) => {
        if (contact) {
            setEditingContact(contact);
            setFormData({
                name: contact.name,
                designation: contact.designation || '',
                phone: contact.phone || '',
                email: contact.email || '',
                notify_email: contact.notify_email,
                notify_whatsapp: contact.notify_whatsapp,
                is_active: contact.is_active,
            });
        } else {
            setEditingContact(null);
            setFormData(initialFormData);
        }
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setEditingContact(null);
        setFormData(initialFormData);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!formData.name.trim()) {
            showToast('Name is required', 'error');
            return;
        }

        if (!formData.phone && !formData.email) {
            showToast('At least phone or email is required', 'error');
            return;
        }

        try {
            setSaving(true);

            if (editingContact) {
                // Update existing contact
                const response = await apiRequest(`/schools/${id}/notification-contacts/${editingContact.id}`, {
                    method: 'PUT',
                    body: formData,
                });
                if (response.success) {
                    showToast('Contact updated successfully', 'success');
                    handleCloseModal();
                    fetchContacts();
                } else {
                    showToast(response.message || 'Failed to update contact', 'error');
                }
            } else {
                // Create new contact
                const response = await apiRequest(`/schools/${id}/notification-contacts`, {
                    method: 'POST',
                    body: formData,
                });
                if (response.success) {
                    showToast('Contact created successfully', 'success');
                    handleCloseModal();
                    fetchContacts();
                } else {
                    showToast(response.message || 'Failed to create contact', 'error');
                }
            }
        } catch (error) {
            console.error('Error saving contact:', error);
            showToast('Failed to save contact', 'error');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (contactId: number) => {
        try {
            const response = await apiRequest(`/schools/${id}/notification-contacts/${contactId}`, {
                method: 'DELETE',
            });
            if (response.success) {
                showToast('Contact deleted successfully', 'success');
                setDeleteConfirm(null);
                fetchContacts();
            } else {
                showToast(response.message || 'Failed to delete contact', 'error');
            }
        } catch (error) {
            console.error('Error deleting contact:', error);
            showToast('Failed to delete contact', 'error');
        }
    };

    const handleToggleActive = async (contact: NotificationContact) => {
        try {
            const response = await apiRequest(`/schools/${id}/notification-contacts/${contact.id}`, {
                method: 'PUT',
                body: { is_active: !contact.is_active },
            });
            if (response.success) {
                showToast(`Contact ${!contact.is_active ? 'activated' : 'deactivated'} successfully`, 'success');
                fetchContacts();
            }
        } catch (error) {
            console.error('Error toggling contact status:', error);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-full">
                <Loader2 className="h-8 w-8 text-brandColor animate-spin" />
            </div>
        );
    }

    return (
        <div>
            {/* Header */}
            <div className="sticky top-0 z-10 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-8 py-4">
                <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                        <div className="p-2 bg-brandColor rounded-lg text-white">
                            <Bell className="w-6 h-6" />
                        </div>
                        <div>
                            <h1 className="text-lg font-semibold text-gray-900 dark:text-white">Notification Center</h1>
                            <p className="text-xs text-gray-500 dark:text-gray-400">Manage contacts for notifications</p>
                        </div>
                    </div>
                    <button
                        onClick={() => handleOpenModal()}
                        className="inline-flex items-center px-4 py-2 bg-brandColor hover:bg-brand-hover text-white font-medium rounded-lg shadow-sm hover:shadow-md transition-all duration-200 text-sm"
                    >
                        <Plus className="w-4 h-4 mr-2" />
                        Add Contact
                    </button>
                </div>
            </div>

            {/* Content */}
            <div className="p-8">
                {contacts.length === 0 ? (
                    <div className="text-center py-12">
                        <Bell className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No Contacts Yet</h3>
                        <p className="text-gray-500 dark:text-gray-400 mb-4">Add contacts to receive notifications via email or WhatsApp.</p>
                        <button
                            onClick={() => handleOpenModal()}
                            className="inline-flex items-center px-4 py-2 bg-brandColor hover:bg-brand-hover text-white font-medium rounded-lg text-sm"
                        >
                            <Plus className="w-4 h-4 mr-2" />
                            Add First Contact
                        </button>
                    </div>
                ) : (
                    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                            <thead className="bg-gray-50 dark:bg-gray-900">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Name</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Contact</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Preferences</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
                                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                {contacts.map((contact) => (
                                    <tr key={contact.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm font-medium text-gray-900 dark:text-white">{contact.name}</div>
                                            {contact.designation && (
                                                <div className="text-xs text-gray-500 dark:text-gray-400">{contact.designation}</div>
                                            )}
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="space-y-1">
                                                {contact.phone && (
                                                    <div className="flex items-center text-sm text-gray-600 dark:text-gray-300">
                                                        <Phone className="w-4 h-4 mr-2 text-gray-400" />
                                                        {contact.phone}
                                                    </div>
                                                )}
                                                {contact.email && (
                                                    <div className="flex items-center text-sm text-gray-600 dark:text-gray-300">
                                                        <Mail className="w-4 h-4 mr-2 text-gray-400" />
                                                        {contact.email}
                                                    </div>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="flex gap-2">
                                                {contact.notify_email && (
                                                    <span className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                                                        <Mail className="w-3 h-3 mr-1" /> Email
                                                    </span>
                                                )}
                                                {contact.notify_whatsapp && (
                                                    <span className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                                                        <MessageCircle className="w-3 h-3 mr-1" /> WhatsApp
                                                    </span>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <button
                                                onClick={() => handleToggleActive(contact)}
                                                className={`inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full transition-colors ${
                                                    contact.is_active
                                                        ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 hover:bg-green-200'
                                                        : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400 hover:bg-gray-200'
                                                }`}
                                            >
                                                {contact.is_active ? 'Active' : 'Inactive'}
                                            </button>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                            <div className="flex justify-end gap-2">
                                                <button
                                                    onClick={() => handleOpenModal(contact)}
                                                    className="p-2 text-gray-400 hover:text-brandColor hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                                                    title="Edit"
                                                >
                                                    <Edit2 className="w-4 h-4" />
                                                </button>
                                                {deleteConfirm === contact.id ? (
                                                    <div className="flex items-center gap-1">
                                                        <button
                                                            onClick={() => handleDelete(contact.id)}
                                                            className="px-2 py-1 text-xs bg-red-500 text-white rounded hover:bg-red-600"
                                                        >
                                                            Confirm
                                                        </button>
                                                        <button
                                                            onClick={() => setDeleteConfirm(null)}
                                                            className="px-2 py-1 text-xs bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
                                                        >
                                                            Cancel
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <button
                                                        onClick={() => setDeleteConfirm(contact.id)}
                                                        className="p-2 text-gray-400 hover:text-red-500 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                                                        title="Delete"
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Modal */}
            {showModal && (
                <div className="fixed inset-0 z-50 overflow-y-auto">
                    <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
                        <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={handleCloseModal}></div>
                        <div className="relative inline-block w-full max-w-md p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white dark:bg-gray-800 shadow-xl rounded-2xl">
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                                {editingContact ? 'Edit Contact' : 'Add New Contact'}
                            </h3>
                            <form onSubmit={handleSubmit} className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        Name *
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.name}
                                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-brandColor"
                                        placeholder="Contact name"
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        Designation
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.designation}
                                        onChange={(e) => setFormData({ ...formData, designation: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-brandColor"
                                        placeholder="e.g., Principal, Admin, Counselor"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        Phone
                                    </label>
                                    <input
                                        type="tel"
                                        value={formData.phone}
                                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-brandColor"
                                        placeholder="Phone number for WhatsApp"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        Email
                                    </label>
                                    <input
                                        type="email"
                                        value={formData.email}
                                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-brandColor"
                                        placeholder="Email address"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Notification Preferences
                                    </label>
                                    <div className="flex gap-4">
                                        <label className="flex items-center cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={formData.notify_email}
                                                onChange={(e) => setFormData({ ...formData, notify_email: e.target.checked })}
                                                className="mr-2 h-4 w-4 text-brandColor focus:ring-brandColor border-gray-300 rounded"
                                            />
                                            <Mail className="w-4 h-4 mr-1 text-blue-500" />
                                            <span className="text-sm text-gray-700 dark:text-gray-300">Email</span>
                                        </label>
                                        <label className="flex items-center cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={formData.notify_whatsapp}
                                                onChange={(e) => setFormData({ ...formData, notify_whatsapp: e.target.checked })}
                                                className="mr-2 h-4 w-4 text-brandColor focus:ring-brandColor border-gray-300 rounded"
                                            />
                                            <MessageCircle className="w-4 h-4 mr-1 text-green-500" />
                                            <span className="text-sm text-gray-700 dark:text-gray-300">WhatsApp</span>
                                        </label>
                                    </div>
                                </div>
                                {editingContact && (
                                    <div>
                                        <label className="flex items-center cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={formData.is_active}
                                                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                                                className="mr-2 h-4 w-4 text-brandColor focus:ring-brandColor border-gray-300 rounded"
                                            />
                                            <span className="text-sm text-gray-700 dark:text-gray-300">Active</span>
                                        </label>
                                    </div>
                                )}
                                <div className="flex justify-end gap-3 mt-6">
                                    <button
                                        type="button"
                                        onClick={handleCloseModal}
                                        className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={saving}
                                        className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-brandColor rounded-lg hover:bg-brand-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                    >
                                        {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                                        {editingContact ? 'Update' : 'Create'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default NotificationCenter;
